// Small pieces the Phase 2 screens share: the Autotask type tag, the OneBill
// state chip, a category's name with its nickname, the category-to-dealer
// suggestion rule, and the names of people and Autotask resources.
import React from "react";
import {
  accountNumberSource,
  matchCategoryToDealer,
  type AutotaskOwner,
  type CustomerBilling,
  type CustomerSummary,
  type Dealer,
  type LensUser,
} from "@phoneware/lens-domain";
import { categoryLinkOf, companyTypeName, type CategoryStatus } from "../api-customers.js";
import { Chip } from "../ui.js";

const TYPE_CLASS: Record<number, string> = { 1: "tag", 2: "tag lead", 3: "tag prospect" };

/** Autotask's company type as the mock tags it: Customer, Prospect, Lead, anything else quiet. */
export function TypeTag({ type }: { type: number }): React.ReactElement {
  return <span className={TYPE_CLASS[type] ?? "tag quiet"}>{companyTypeName(type)}</span>;
}

export function BillingChip({ billing, testId }: { billing: CustomerBilling; testId?: string }): React.ReactElement {
  if (billing.state === "linked") {
    return (
      <Chip kind="ok" icon="check" testId={testId} status="linked">
        Linked
      </Chip>
    );
  }
  if (billing.state === "none") {
    return (
      <Chip kind="quiet" testId={testId} status="none">
        No account
      </Chip>
    );
  }
  return (
    <Chip kind="warn" icon="triangle-alert" testId={testId} status="unreviewed">
      Not reviewed
    </Chip>
  );
}

/** The Autotask Number OneBill bills under, tagged where it came from: Omni for a PW number Omni gave, RPP for one from RPP. */
export function AccountNumber({ number, testId }: { number: string | null | undefined; testId?: string }): React.ReactElement {
  const n = number?.trim() ?? "";
  if (!n) {
    return (
      <span className="acno none" data-testid={testId} data-source="none">
        None in Autotask
      </span>
    );
  }
  const source = accountNumberSource(n);
  return (
    <span className="acno" data-testid={testId} data-source={source}>
      <b className="acno-n">{n}</b>
      {source === "other" ? null : <span className={`tag ${source}`}>{source === "omni" ? "Omni" : "RPP"}</span>}
    </span>
  );
}

/** What the two account number tags mean. */
export function AccountNumberLegend(): React.ReactElement {
  return (
    <div className="legend" data-testid="account-legend">
      <span>
        <span className="tag omni">Omni</span>A PW number Omni gave a new customer
      </span>
      <span>
        <span className="tag rpp">RPP</span>A number from RPP
      </span>
    </div>
  );
}

export function CategoryName({ name, nickname }: { name: string; nickname: string | null }): React.ReactElement {
  return (
    <>
      {name}
      {nickname ? <span className="nick">{nickname}</span> : null}
    </>
  );
}

export const BILL_VIA: Record<Extract<CustomerBilling, { state: "linked" }>["via"], string> = {
  billing_map: "From the billing map",
  name_exact: "Exact name match",
  name_near_exact: "Near-exact name match",
  ops: "Linked by ops",
  order: "Created by its first order",
  account_number: "Account number match",
};

/** "Name 67%" or "Same phone", for a suggested match. */
export function whyText(c: { why: "name" | "phone" | "number" | "parent" | string; score: number }): string {
  if (c.why === "phone") return "Same phone";
  if (c.why === "number") return "Account number";
  if (c.why === "parent") return "Parent account";
  return `Name ${Math.round(c.score * 100)}%`;
}

export interface Suggestion {
  dealer: Dealer;
  /** Why the shared matching rule offers this dealer, in a sentence. */
  why: string;
}

/**
 * The dealer the plan's matching rule (3.2) suggests for a category nobody holds, among dealers without a category.
 * Computed with the same pure function the sync uses, so the screen and the server agree.
 */
export function suggestDealer(category: CategoryStatus, dealers: Dealer[]): Suggestion | null {
  if (category.dealerId || !category.active) return null;
  const free = dealers.filter((d) => categoryLinkOf(d) === null);
  const result = matchCategoryToDealer({ id: category.id, name: category.name, nickname: category.nickname }, free.map((d) => ({ id: d.id, name: d.name, nsReseller: d.nsReseller })));
  if (result.matched) {
    const dealer = free.find((d) => d.id === result.matched?.dealerId);
    return dealer ? { dealer, why: `${dealer.name} matches ${category.name} by name; the next sync links them.` } : null;
  }
  const first = result.suggestions[0];
  const dealer = first ? free.find((d) => d.id === first.dealerId) : undefined;
  if (!first || !dealer) return null;
  return {
    dealer,
    why:
      first.reason === "nickname_prefix" && category.nickname
        ? `${dealer.name} starts with the nickname ${category.nickname}.`
        : `${dealer.name}'s name contains ${category.name}.`,
  };
}

/** A person's name from their email, when the caller can list people; otherwise the email. */
export function personName(users: LensUser[] | null, email: string | null): string {
  if (!email) return "Omni";
  return users?.find((u) => u.email === email)?.name ?? email;
}

/** An Autotask resource's name: from the people ops can choose, or the dealer's own account manager. */
export function resourceName(owners: AutotaskOwner[], resourceId: number | null): string | null {
  if (resourceId === null) return null;
  return owners.find((o) => o.resourceId === resourceId)?.name ?? `Autotask resource ${resourceId}`;
}

/** "Temecula, CA · Company 9123" */
export function placeLine(c: Pick<CustomerSummary, "address" | "autotaskCompanyId">): string {
  return [c.address ? `${c.address.city}, ${c.address.state}` : null, `Company ${c.autotaskCompanyId}`].filter(Boolean).join(" \u00b7 ");
}
